import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { formatDate } from "@/lib/date.utils";
import { cn } from "@/lib/utils";
import { ArrowUpRight, CalendarClock, CircleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import type { CreditCardOverview, CreditCardOverviewCard } from "@/types";

interface CardOverviewGridProps {
  overview: CreditCardOverview | undefined;
  isLoading: boolean;
  month: string;
}

function BillStatusBadge({ card }: { card: CreditCardOverviewCard }) {
  if (card.bill.isGenerated) {
    return (
      <Badge className="bg-success/10 text-success border-success/30 hover:bg-success/10">
        Bill generated
      </Badge>
    );
  }
  return (
    <Badge className="bg-warning/10 text-warning border-warning/30 hover:bg-warning/10 gap-1">
      <CircleAlert className="size-3" />
      Approx
    </Badge>
  );
}

export function CardOverviewGrid({ overview, isLoading }: CardOverviewGridProps) {
  if (isLoading && !overview) {
    return (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }
  if (!overview || overview.cards.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        No credit card accounts found. Add one under Accounts.
      </div>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {overview.cards.map((card) => {
        const billAmount = card.bill.isGenerated ? (card.bill.amount ?? 0) : card.bill.approxAmount;
        const fullyPaid =
          card.bill.paidAmount > 0 && card.bill.paidAmount >= billAmount && billAmount > 0;

        return (
          <div
            key={card.accountId}
            className="rounded-lg border border-border bg-background p-4 hover:bg-accent/20"
          >
            <div className="flex items-start justify-between gap-2">
              <Link to={`/accounts/${card.accountId}`} className="min-w-0 flex items-center gap-2">
                {card.iconUrl ? (
                  <img src={card.iconUrl} alt="" className="size-8 shrink-0 rounded" />
                ) : null}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {card.accountName}
                    {card.isArchived && (
                      <span className="ml-1 text-xs font-normal text-muted-foreground/70">
                        (Archived)
                      </span>
                    )}
                  </p>
                  {card.lastFourAccountNumber && (
                    <p className="text-xs text-muted-foreground">
                      •••• {card.lastFourAccountNumber}
                    </p>
                  )}
                </div>
              </Link>
              <BillStatusBadge card={card} />
            </div>

            <div className="mt-3 flex items-baseline justify-between gap-2">
              <div>
                <p className="text-xs text-muted-foreground">
                  {card.bill.isGenerated ? "Bill amount" : "Approx bill (as of now)"}
                </p>
                <p
                  className={cn(
                    "text-lg font-medium tabular-nums",
                    !card.bill.isGenerated && "text-warning",
                  )}
                >
                  <AmountDisplay amount={billAmount} />
                </p>
                {!card.bill.isGenerated && (
                  <p className="text-xs text-muted-foreground/70">
                    cycle {formatDate(card.cycleStart)} → {formatDate(card.cycleEnd)}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Spend</p>
                <p className="text-sm font-medium tabular-nums">
                  <AmountDisplay amount={card.totalSpend} />
                </p>
                {card.totalCredits > 0 && (
                  <p className="text-xs text-success tabular-nums">
                    −<AmountDisplay amount={card.totalCredits} /> credits
                  </p>
                )}
                {card.totalRepayments > 0 && (
                  <p
                    className="text-xs text-success tabular-nums"
                    title="Payments transferred from another account — they reduce this bill but are not cashback or refunds."
                  >
                    −<AmountDisplay amount={card.totalRepayments} /> repaid
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="size-3.5" />
                {card.bill.dueDate ? `Due ${formatDate(card.bill.dueDate)}` : "Due date unknown"}
              </span>
              <span className="flex items-center gap-2">
                {card.bill.paidAmount > 0 && (
                  <span className={cn("tabular-nums", fullyPaid && "text-success")}>
                    {fullyPaid ? (
                      "Paid"
                    ) : (
                      <>
                        <AmountDisplay amount={card.bill.paidAmount} /> paid
                      </>
                    )}
                  </span>
                )}
                <Link
                  to={`/credit-cards?tab=transactions&accountId=${card.accountId}&startDate=${card.cycleStart.slice(0, 10)}&endDate=${card.cycleEnd.slice(0, 10)}`}
                  className="-mx-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
                  title={`View ${card.accountName} transactions for this cycle`}
                >
                  txns
                  <ArrowUpRight className="size-3" />
                </Link>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
